'use client';

import type { Id } from '@/convex/_generated/dataModel';
import type { BrowserUploadIdentity } from './browser-upload-hash';
import {
  isStoredByEarlierPut,
  newBrowserUploadAttemptId,
} from './browser-upload-session-client';
import { putSignedBrowserUpload } from './upload-via-proxy';
import { putSignedBrowserUploadWithProgress } from './upload-via-proxy-with-progress';

type ReferenceCategory = 'product' | 'design' | 'pattern' | 'color';

/** The composer's two Convex calls (see media/uploads start/completeComposerPutUpload). */
export type ComposerPutUploadCalls = {
  start: (args: {
    orgSlug: string;
    organizationId?: Id<'organizations'>;
    mediaType: 'image' | 'video' | 'audio' | 'file' | 'three-d';
    filename?: string;
    flow?: string;
    contentType: string;
    contentHash: string;
    contentMd5: string;
    sizeBytes: number;
    attemptId?: string;
  }) => Promise<{
    sessionId: Id<'browserUploadSessions'>;
    token: string;
    storageKey: string;
    owner: boolean;
    uploadUrl: string;
    uploadHeaders: Record<string, string>;
  }>;
  complete: (args: {
    sessionId: Id<'browserUploadSessions'>;
    token: string;
    orgSlug: string;
    orgThreadId?: Id<'orgThreads'>;
    upload: {
      storageKey: string;
      uploadId?: string;
      filename?: string;
      mimeType?: string;
      sizeBytes?: number;
      referenceCategory?: ReferenceCategory;
    };
  }) => Promise<
    | {
        objectEtag: string;
        registered: {
          fileId: Id<'workspaceFiles'>;
          path: string;
          r2Key: string;
        } | null;
      }
    /** A completion this session can never get past (one of the terminal codes). */
    | { refused: string }
  >;
  abort: (args: {
    sessionId: Id<'browserUploadSessions'>;
    token: string;
    attemptId?: string;
  }) => Promise<unknown>;
};

/** A started session whose bytes are stored; a retry completes it again. */
export type ComposerPutSession = Awaited<
  ReturnType<ComposerPutUploadCalls['start']>
> & { attemptId: string };

/** Completion errors a retry of the same session can never get past. */
const TERMINAL_COMPLETE_ERRORS = [
  'BROWSER_UPLOAD_NOT_UPLOADABLE',
  'BROWSER_UPLOAD_SESSION_EXPIRED',
  'BROWSER_UPLOAD_SESSION_DENIED',
  'BROWSER_UPLOAD_CONTENT_MISMATCH',
  'BROWSER_UPLOAD_OBJECT_MISSING',
  'BROWSER_UPLOAD_STORAGE_KEY_MISMATCH',
];

function isTerminalCompleteError(error: unknown): boolean {
  const data =
    error && typeof error === 'object' && 'data' in error ? error.data : '';
  const text = `${typeof data === 'string' ? data : ''} ${
    error instanceof Error ? error.message : String(error)
  }`;
  return TERMINAL_COMPLETE_ERRORS.some((code) => text.includes(code));
}

/**
 * One composer file: start (reserve + claim + presign), the signed PUT that
 * R2 verifies at write time, then complete (verify + register). Returns the
 * storage key and, unless registration was deferred, the registered file.
 *
 * Once the bytes are stored the session is handed to `onSession`. If
 * `complete`'s response is lost, the retry passes it back as `resume` and
 * completes the SAME session (replay-safe, registration keyed by its storage
 * key), so a retry never uploads or registers a second file.
 */
export async function uploadComposerPut(args: {
  file: File;
  contentType: string;
  /** Called only when a new session starts: `start` pins the content identity. */
  identity: () => Promise<BrowserUploadIdentity>;
  reserve: {
    orgSlug: string;
    organizationId?: Id<'organizations'>;
    mediaType: 'image' | 'video' | 'audio' | 'file' | 'three-d';
    flow: string;
  };
  registration: {
    orgThreadId?: Id<'orgThreads'>;
    uploadId: string;
    referenceCategory?: ReferenceCategory;
  };
  calls: ComposerPutUploadCalls;
  signal: AbortSignal;
  resume?: ComposerPutSession;
  onSession?: (session: ComposerPutSession | null) => void;
  /** Bytes sent so far (XHR); without it the PUT goes through fetch. */
  onProgress?: (loadedBytes: number, totalBytes: number) => void;
}) {
  const { file, contentType, reserve, registration, calls, signal } = args;
  let stored = args.resume;
  if (!stored) {
    const attemptId = newBrowserUploadAttemptId();
    const identity = await args.identity();
    signal.throwIfAborted();
    const started = {
      ...(await calls.start({
        ...reserve,
        filename: file.name,
        contentType,
        ...identity,
        attemptId,
      })),
      attemptId,
    };
    const abortOwned = async () => {
      if (started.owner)
        await calls
          .abort({
            sessionId: started.sessionId,
            token: started.token,
            attemptId,
          })
          .catch(() => undefined);
    };
    let response: Response;
    try {
      response = args.onProgress
        ? await putSignedBrowserUploadWithProgress(
            started.uploadUrl,
            file,
            contentType,
            signal,
            started.uploadHeaders,
            args.onProgress
          )
        : await putSignedBrowserUpload(
            started.uploadUrl,
            file,
            contentType,
            signal,
            started.uploadHeaders
          );
    } catch (error) {
      await abortOwned();
      throw error;
    }
    if (!response.ok && !isStoredByEarlierPut(started, response.status)) {
      await abortOwned();
      throw new Error(`UPLOAD_FAILED: UPLOAD_HTTP_${response.status}`);
    }
    stored = started;
    args.onSession?.(stored);
  }
  let completed: Awaited<ReturnType<ComposerPutUploadCalls['complete']>>;
  try {
    completed = await calls.complete({
      sessionId: stored.sessionId,
      token: stored.token,
      orgSlug: reserve.orgSlug,
      ...(registration.orgThreadId
        ? { orgThreadId: registration.orgThreadId }
        : {}),
      upload: {
        storageKey: stored.storageKey,
        uploadId: registration.uploadId,
        filename: file.name,
        mimeType: contentType,
        sizeBytes: file.size,
        ...(registration.referenceCategory
          ? { referenceCategory: registration.referenceCategory }
          : {}),
      },
    });
  } catch (error) {
    if (isTerminalCompleteError(error)) args.onSession?.(null);
    throw error;
  }
  args.onSession?.(null);
  if ('refused' in completed)
    throw new Error(`UPLOAD_FAILED: ${completed.refused}`);
  return { storageKey: stored.storageKey, registered: completed.registered };
}
